import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled from "styled-components";

import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import { configToS3BucketUrl } from "src/utils/aws";
import { IStore } from "src/core/shared/store";
import { ITerminology } from "src/core/shared/api";

import { PaymentMethodCard } from "../PaymentMethodCard";
import { IPaymentCard } from "../../api/payment.api";

interface TransferPaymentMethodBranchesModalProps extends Omit<BasicModalProps, "title"> {
  terminology: ITerminology;
  onSelect: (id: IPaymentCard["id"]) => void;
  activeCardId?: IPaymentCard["id"];
  paymentMethods?: IPaymentCard[];
  loading?: boolean;
}

/**
 * Transfer payment method branches modal
 */
const TransferPaymentMethodBranchesModal: React.ReactFCWithChildren<TransferPaymentMethodBranchesModalProps> = ({
  loading,
  open,
  onDismiss,
  paymentMethods,
  terminology,
  activeCardId,
  onSelect
}) => {
  const { awsConfig } = useMappedState(({ global }: IStore) => ({ awsConfig: global.settings?.aws }));
  const s3BucketBaseUrl = React.useMemo(() => configToS3BucketUrl(awsConfig), [ awsConfig ]);

  return (
    <BasicModal
      open={open}
      onDismiss={onDismiss}
      title="Change card"
      body={`Select a card that you wish to transfer all of the assigned ${terminology.drop.plural.text} to.`}
    >
      <StyledTransferPaymentMethodBranchesModal>
        {loading ? (
          <LoadingSpinner
            type="circular"
            position="inline"
          />
        ) : (
          <>
            {!paymentMethods || paymentMethods.length === 0 ? (
              <Text
                className="no-payment-methods"
                fontSize="h4"
              >
                You have no payment methods
              </Text>
            ) : (
              <ul>
                {paymentMethods.map(paymentMethod => (
                  <li key={paymentMethod.id}>
                    <PaymentMethodCard
                      s3BucketBaseUrl={s3BucketBaseUrl}
                      cardDetails={paymentMethod}
                      activeCardId={activeCardId}
                      onClick={() => onSelect(paymentMethod.id)}
                      inline
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </StyledTransferPaymentMethodBranchesModal>
    </BasicModal>
  );
};

export default TransferPaymentMethodBranchesModal;

const StyledTransferPaymentMethodBranchesModal = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 8rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ul {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    li {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: 0.8rem;
      width: 100%;

      &:first-child {
        padding-top: 0;
      }
    }
  }
`;
